import { CSSProperties } from '@mui/material/styles/createMixins';
import theme from '@src/theme';

import { CHAIN_ID } from '@constants/common/chain.constants';

import { VotingFarmStatus } from '@dto/vaults/voting.dto';

export const TABLE_HEIGHT = 580;

export enum Tab {
  Voting = 'voting',
  Statistics = 'statistics',
}

export const TAB_OPTIONS = [
  {
    label: 'Voting List',
    value: Tab.Voting,
  },
  {
    label: 'Farm Statistics',
    value: Tab.Statistics,
  },
];

export enum InnerTab {
  Active = 'active',
  Completed = 'completed',
}

export enum ActiveProposalsKeys {
  Farm = 'farm',
  Chain = 'chain',
  ProtocolName = 'protocolName',
  CreationDate = 'creationDate',
  MarketCap = 'marketCap',
  Closing = 'closing',
  Volume = 'volume',
  Liquidity = 'liquidity',
  Votes = 'votes',
  Action = 'action',
}

export const ACTIVE_PROPOSALS_HEADER: Record<ActiveProposalsKeys, string> = {
  [ActiveProposalsKeys.Farm]: 'Farm',
  [ActiveProposalsKeys.Chain]: 'Chain',
  [ActiveProposalsKeys.ProtocolName]: 'Protocol name',
  [ActiveProposalsKeys.CreationDate]: 'Creation date',
  [ActiveProposalsKeys.Closing]: 'Closing',
  [ActiveProposalsKeys.Volume]: 'Volume',
  [ActiveProposalsKeys.MarketCap]: 'Market Cap',
  [ActiveProposalsKeys.Liquidity]: 'Liquidity',
  [ActiveProposalsKeys.Votes]: 'Votes',
  [ActiveProposalsKeys.Action]: '',
};

export enum CompletedProposalsKeys {
  Farm = 'farm',
  Chain = 'chain',
  ProtocolName = 'protocolName',
  CreationDate = 'creationDate',
  Closing = 'closing',
  Volume = 'volume',
  Liquidity = 'liquidity',
  Votes = 'votes',
  Status = 'status',
  MarketCap = 'marketCap',
}

export const COMPLETED_PROPOSALS_HEADER: Record<
  CompletedProposalsKeys,
  string
> = {
  [CompletedProposalsKeys.Farm]: 'Farm',
  [CompletedProposalsKeys.Chain]: 'Chain',
  [CompletedProposalsKeys.ProtocolName]: 'Protocol name',
  [CompletedProposalsKeys.CreationDate]: 'Creation date',
  [CompletedProposalsKeys.Closing]: 'Closing',
  [CompletedProposalsKeys.Volume]: 'Volume',
  [CompletedProposalsKeys.Liquidity]: 'Liquidity',
  [CompletedProposalsKeys.Votes]: 'Votes',
  [CompletedProposalsKeys.Status]: 'Status',
  [CompletedProposalsKeys.MarketCap]: 'Market Cap',
};

export enum FarmStatisticsKeys {
  Chain = 'chain',
  ProtocolName = 'protocolName',
  AddedFarms = 'addedFarms',
  ActiveProposals = 'activeProposals',
  FarmsCanceled = 'farmsCanceled',
  Votes = 'votes',
}

export const FARM_STATISTICS_HEADER: Record<FarmStatisticsKeys, string> = {
  [FarmStatisticsKeys.Chain]: 'Chain',
  [FarmStatisticsKeys.ProtocolName]: 'Protocol name',
  [FarmStatisticsKeys.AddedFarms]: 'Added Farms',
  [FarmStatisticsKeys.ActiveProposals]: 'Active Proposals',
  [FarmStatisticsKeys.FarmsCanceled]: 'Farms Canceled',
  [FarmStatisticsKeys.Votes]: 'Votes',
};

export const STATUS_TITLE: Record<VotingFarmStatus, string> = {
  [VotingFarmStatus.Added]: 'Added',
  [VotingFarmStatus.Declined]: 'Declined',
  [VotingFarmStatus.Active]: 'Active',
  [VotingFarmStatus.BlackList]: 'BlackList',
  [VotingFarmStatus.Preparing]: 'Preparing',
};

export const STATUS_TOOLTIP: Record<VotingFarmStatus, string> = {
  [VotingFarmStatus.Added]: 'The farm was successfully added to the protocol.',
  [VotingFarmStatus.Declined]:
    'Not enough votes were gathered to add this farm, so it was rejected.',
  [VotingFarmStatus.BlackList]:
    'This farm is considered non-compliant and cannot be added to the protocol.',
  [VotingFarmStatus.Preparing]: '',
  [VotingFarmStatus.Active]: '',
};

export const STATUS_COLOR: Record<VotingFarmStatus, CSSProperties['color']> = {
  [VotingFarmStatus.Added]: theme.accent.a1,
  [VotingFarmStatus.Declined]: theme.accent.a6,
  [VotingFarmStatus.Active]: theme.accent.a1,
  [VotingFarmStatus.BlackList]: theme.accent.a4,
  [VotingFarmStatus.Preparing]: theme.accent.a1,
};

export const ADD_REQUEST_BASIC_STATS = [
  {
    title: 'Farm Name',
    keyValue: 'name',
  },
  {
    title: 'Protocol',
    keyValue: 'protocol',
  },
  {
    title: 'Chain',
    keyValue: 'chain',
  },
  {
    title: 'Volume',
    keyValue: 'volume',
  },
  {
    title: 'Market cap',
    keyValue: 'marketCap',
  },
  {
    title: 'TVL',
    keyValue: 'tvl',
  },
];

export const ADD_REQUEST_STATS = [
  {
    title: 'Farm Name',
    keyValue: 'name',
  },
  {
    title: 'Protocol',
    keyValue: 'protocol',
  },
  {
    title: 'Creation date',
    keyValue: 'creationDate',
  },
  {
    title: 'Closing',
    keyValue: 'closing',
  },
  {
    title: 'Chain',
    keyValue: 'chain',
  },
  {
    title: 'Volume',
    keyValue: 'volume',
  },
  {
    title: 'Market cap',
    keyValue: 'marketCap',
  },
  {
    title: 'TVL',
    keyValue: 'tvl',
  },
];

export enum SortVotingOrderBy {
  StartVotingDate = 'start_voting_date',
  Tvl = 'tvl',
  Volume24h = 'volume_24h',
  MarketCap = 'market_cap',
  Votes = 'votes',
}

export const SORT_BY_MAP: Record<string, SortVotingOrderBy> = {
  [ActiveProposalsKeys.CreationDate]: SortVotingOrderBy.StartVotingDate,
  [ActiveProposalsKeys.Liquidity]: SortVotingOrderBy.Tvl,
  [ActiveProposalsKeys.Volume]: SortVotingOrderBy.Volume24h,
  [ActiveProposalsKeys.MarketCap]: SortVotingOrderBy.MarketCap,
  [ActiveProposalsKeys.Votes]: SortVotingOrderBy.Votes,
};

export enum SortStatisticsOrderBy {
  AddedFarms = 'added_farms',
  ActiveProposal = 'active_proposal',
  Votes = 'votes',
}

export const SORT_STATS_BY_MAP: Record<string, SortStatisticsOrderBy> = {
  [FarmStatisticsKeys.AddedFarms]: SortStatisticsOrderBy.AddedFarms,
  [FarmStatisticsKeys.ActiveProposals]: SortStatisticsOrderBy.ActiveProposal,
  [FarmStatisticsKeys.Votes]: SortStatisticsOrderBy.Votes,
};

export const FARM_ERROR_TEXT_MAP: Partial<Record<VotingFarmStatus, string>> = {
  [VotingFarmStatus.Active]:
    'The proposal has been created! Please make sure to up vote it.',
  [VotingFarmStatus.Added]: 'This farm is already available in the vaults.',
};

export enum DexscreenerChainId {
  Optimism = 'optimism',
  Bsc = 'bsc',
  Sonic = 'sonic',
  Manta = 'manta',
  Pulsechain = 'pulsechain',
  Base = 'base',
  Arbitrum = 'arbitrum',
  Avalanche = 'avalanche',
}

export const DEXSCREENER_CHAIN_ID_MAP: {
  [key in DexscreenerChainId]: CHAIN_ID;
} = {
  [DexscreenerChainId.Optimism]: CHAIN_ID.OPTIMISM,
  [DexscreenerChainId.Bsc]: CHAIN_ID.BSC,
  [DexscreenerChainId.Sonic]: CHAIN_ID.SONIC,
  [DexscreenerChainId.Manta]: CHAIN_ID.MANTA,
  [DexscreenerChainId.Pulsechain]: CHAIN_ID.PULSE,
  [DexscreenerChainId.Base]: CHAIN_ID.BASE,
  [DexscreenerChainId.Arbitrum]: CHAIN_ID.ARBITRUM,
  [DexscreenerChainId.Avalanche]: CHAIN_ID.AVALANCHE,
};
